import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Hero from "@/components/landing/Hero";
import InstantQrPrototypePanel from "@/components/landing/InstantQrPrototypePanel";
import LandingLoadScreen from "@/components/landing/LandingLoadScreen";
import "@/components/landing/landing-redesign.css";
import "@/components/landing/landing-redesign-sections.css";
import SignedInCtaDialog from "@/components/landing/SignedInCtaDialog";
import InboundMagic from "@/components/landing/InboundMagic";
import UseCases from "@/components/landing/UseCases";
import Pricing from "@/components/landing/Pricing";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { useLandingInstantLink } from "@/hooks/use-landing-instant-link";
import { useLandingAuthState } from "@/hooks/use-landing-auth-state";
import { useLandingIntro } from "@/hooks/use-landing-intro";
import { clearSignedInBrowserState } from "@/lib/browser-logout-state";
import { captureShareLink, getPreferencesBootstrapDecision, getRuntimeConfig, request, trackEvent } from "@/lib/api";
import { referralCodeFromLocation, safeMemberDestination, funnelVisitorId } from "@/lib/share-link-funnel";
import { useI18n } from "@/lib/i18n";
import { shouldEnableInstantQrPrototype } from "@/lib/instant-qr-prototype";
import {
  buildLandingIntentPath,
  buildSignedInLandingDialogState,
  getLandingCtaAnalyticsLabel,
  LANDING_SIGNED_IN_NAV_ITEMS,
  shouldRestartLandingInstantQr,
} from "@/lib/landing-cta";
import {
  DEFAULT_LANGUAGE_CODE,
  getLanguageLabel,
  getUiLanguageCatalog,
  normalizeUiLanguageCode,
  resolveSuggestedUiLanguageCode,
  resolveSuggestedUiLanguageSource,
} from "@/lib/languages";
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import { hasAuthenticatedStoredSupabaseSession } from "@/lib/preferences-entry-gate";
import { getShareLinkEntry, rememberShareLinkEntry } from "@/lib/share-link-entry";
import { resetBrowserSupabaseSession } from "@/lib/supabase-session";

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const referralCode = referralCodeFromLocation(location);
  const { locale: routeLocale = "" } = useParams();
  const { locale, setLocale, t, tx } = useI18n();
  const prototypeEnabled = shouldEnableInstantQrPrototype({
    onboardingDesignMode: ONBOARDING_DESIGN_MODE,
  });
  const { showIntro, introPending, handleIntroExit } = useLandingIntro();
  const { authState, setAuthState, refreshOwnedAccount } = useLandingAuthState({ designModeEnabled: prototypeEnabled });
  const hasBrowserAccount = authState.isAuthenticated || Boolean(authState.ownedAccount);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogLogoutLoading, setDialogLogoutLoading] = useState(false);
  const [pendingIntent, setPendingIntent] = useState(null);
  const [accountDestination, setAccountDestination] = useState("link");
  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const [suggestedLocale, setSuggestedLocale] = useState("");
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const [shareLinkEntry, setShareLinkEntry] = useState(() => getShareLinkEntry());
  const [shareLinkCapturePending, setShareLinkCapturePending] = useState(() => (
    prototypeEnabled ? false : Boolean(referralCode)
  ));
  const viewedLocalePromptKeysRef = useRef(new Set());
  const browserLanguages = typeof navigator !== "undefined" && Array.isArray(navigator.languages)
    ? navigator.languages
    : (typeof navigator !== "undefined" && navigator.language ? [navigator.language] : []);
  const instantLink = useLandingInstantLink({
    designModeEnabled: prototypeEnabled,
    productionEnabled: !prototypeEnabled
      && authState.checked
      && !hasBrowserAccount
      && runtimeConfig?.landingInstantLinkEnabled === true,
    onFallback: handleInstantLinkFallback,
  });

  useLayoutEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const previousTitle = document.title;
    document.title = `Min — ${t("landing.hero_line_1")} ${t("landing.hero_line_2")}`;
    return () => {
      document.title = previousTitle;
    };
  }, [locale, t]);

  useEffect(() => {
    if (!routeLocale) {
      return;
    }
    setLocale(routeLocale);
  }, [routeLocale, setLocale]);

  useEffect(() => {
    if (prototypeEnabled) {
      return;
    }
    const code = referralCode;
    trackEvent("landing_page_visit", {
      code,
      entry_surface: code ? "share_link" : "direct",
    });
  }, [prototypeEnabled, referralCode]);

  useEffect(() => {
    if (prototypeEnabled) {
      setShareLinkCapturePending(false);
      return undefined;
    }

    let cancelled = false;

    async function captureLandingShareLink() {
      const code = referralCode;
      if (!code) {
        setShareLinkCapturePending(false);
        return;
      }
      setShareLinkCapturePending(true);
      try {
        const payload = await captureShareLink(code, { track: true, entrySurface: "homepage_referral" });
        if (cancelled) {
          return;
        }
        rememberShareLinkEntry(payload?.shareLink || null);
        setShareLinkEntry(getShareLinkEntry());
      } catch {
        if (!cancelled) setShareLinkEntry(null);
      } finally {
        if (!cancelled) {
          setShareLinkCapturePending(false);
        }
      }
    }

    void captureLandingShareLink();

    return () => {
      cancelled = true;
    };
  }, [prototypeEnabled, referralCode]);

  useEffect(() => {
    if (prototypeEnabled || !referralCode || !authState.checked || !authState.isAuthenticated) return;
    let cancelled = false;
    request("/api/share-links/member-destination", {
      method: "POST", body: JSON.stringify({ code: referralCode, visitorId: funnelVisitorId() }),
    }).then((payload) => {
      const destination = safeMemberDestination(payload?.destination);
      if (!cancelled && destination) navigate(destination, { replace: true });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [authState.checked, authState.isAuthenticated, navigate, prototypeEnabled, referralCode]);

  useEffect(() => {
    if (prototypeEnabled) {
      setRuntimeConfig(null);
      return undefined;
    }

    let cancelled = false;
    async function loadRuntimeConfig() {
      try {
        const payload = await getRuntimeConfig();
        if (!cancelled) {
          setRuntimeConfig(payload);
        }
      } catch {
        if (!cancelled) {
          setRuntimeConfig(null);
        }
      }
    }
    void loadRuntimeConfig();
    return () => {
      cancelled = true;
    };
  }, [prototypeEnabled]);

  useEffect(() => {
    setSuggestedLocale(resolveSuggestedUiLanguageCode({
      shareLinkEntry,
      runtimeConfig,
      browserLanguages,
      shareLinkCapturePending,
    }));
  }, [browserLanguages, runtimeConfig, shareLinkCapturePending, shareLinkEntry]);

  function trackLandingCta(intent) {
    if (!prototypeEnabled && String(intent?.type || "start").trim().toLowerCase() !== "sign-in") {
      const normalizedPlan = typeof intent?.plan === "string" && intent.plan ? intent.plan : "free";
      trackEvent("cta_click", {
        cta_label: getLandingCtaAnalyticsLabel(normalizedPlan),
        code: referralCode,
      });
    }
  }

  function continueIntent(intent, { track = true } = {}) {
    if (track) trackLandingCta(intent);
    navigate(buildLandingIntentPath(intent));
  }

  const signedInNavItems = useMemo(
    () => (authState.ownedAccount && !authState.isAuthenticated
      ? [{ key: "configure", label: "Configure Min", onClick: () => navigate("/auth?context=linked") }]
      : authState.isAuthenticated
      ? LANDING_SIGNED_IN_NAV_ITEMS.map((item) => ({
          ...item,
          onClick: () => navigate(item.href),
        }))
      : []),
    [authState.isAuthenticated, authState.ownedAccount, navigate],
  );

  async function handleInstantLinkFallback(error) {
    const intent = { type: "start", plan: "fun" };
    if (["existing_pool_claim", "existing_account"].includes(error?.message)) {
      const decision = await refreshOwnedAccount();
      if (decision === false) return;
      void openSignedInDialog({ ...intent, returnToLanding: canRestartInstantQr(intent) }, decision);
      return;
    }
    continueIntent(intent, { track: false });
  }

  function canRestartInstantQr(intent) {
    return shouldRestartLandingInstantQr(intent, {
      enabled: runtimeConfig?.landingInstantLinkEnabled === true,
      desktop: window.matchMedia?.("(min-width: 960px)").matches === true,
    });
  }

  async function openSignedInDialog(intent, knownDecision = null) {
    setPendingIntent(intent);
    setDialogOpen(true);
    setDialogLoading(true);
    setAccountDestination("link");

    try {
      const decision = knownDecision || await getPreferencesBootstrapDecision();
      setAccountDestination(decision?.destination || "link");
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (/authentication required/i.test(message)) {
        setDialogOpen(false);
        setDialogLoading(false);
        setPendingIntent(null);
        continueIntent(intent);
        return;
      }
      setAccountDestination("link");
    } finally {
      setDialogLoading(false);
    }
  }

  function maybeInterceptIntent(intent) {
    const hasAuthenticatedSession = hasBrowserAccount || hasAuthenticatedStoredSupabaseSession();
    if (!hasAuthenticatedSession) {
      continueIntent(intent);
      return;
    }
    void openSignedInDialog({ ...intent, returnToLanding: Boolean(authState.ownedAccount) && canRestartInstantQr(intent) });
  }

  function handleStart(plan = "fun") {
    const normalizedPlan = typeof plan === "string" ? plan : "fun";
    maybeInterceptIntent({
      type: "start",
      plan: normalizedPlan,
    });
  }

  function handleSignIn() {
    maybeInterceptIntent({
      type: "sign-in",
    });
  }

  function handleInstantLinkAwaken() {
    trackLandingCta({ type: "start", plan: "fun" });
    instantLink.onAwaken?.();
  }

  async function handleLogoutAndContinue() {
    try {
      setDialogLogoutLoading(true);
      await resetBrowserSupabaseSession();
      clearSignedInBrowserState();
      setDialogOpen(false);
      if (pendingIntent?.returnToLanding) {
        // Renew only this browser after explicit sign-out, never the WhatsApp session.
        window.location.reload();
      } else {
        setAuthState({ checked: true, isAuthenticated: false, email: "", ownedAccount: null });
        continueIntent(pendingIntent);
      }
    } finally {
      setDialogLogoutLoading(false);
      setPendingIntent(null);
    }
  }

  const dialogState = buildSignedInLandingDialogState({
    email: authState.email,
    accountDestination,
    anonymousAccount: Boolean(authState.ownedAccount) && !authState.isAuthenticated,
    pendingIntent,
  });
  const shareLinkLanguageCode = shareLinkEntry?.uiLanguageHintCode || shareLinkEntry?.deliveryLanguageCode || "";
  const suggestedLocaleSource = resolveSuggestedUiLanguageSource({
    shareLinkEntry,
    runtimeConfig,
  });
  const showLocalePrompt = !dismissedSuggestion
    && suggestedLocale
    && suggestedLocale !== DEFAULT_LANGUAGE_CODE
    && suggestedLocale !== locale;

  useEffect(() => {
    if (!showLocalePrompt || prototypeEnabled) {
      return;
    }
    const promptKey = `${suggestedLocale}:${suggestedLocaleSource}`;
    if (viewedLocalePromptKeysRef.current.has(promptKey)) {
      return;
    }
    viewedLocalePromptKeysRef.current.add(promptKey);
    trackEvent("locale_prompt_viewed", {
      suggested_language_code: suggestedLocale,
      source: suggestedLocaleSource,
      country_code: runtimeConfig?.countryCode || "",
      entry_surface: shareLinkLanguageCode ? "share_link" : "direct",
    });
  }, [
    runtimeConfig?.countryCode,
    prototypeEnabled,
    shareLinkLanguageCode,
    showLocalePrompt,
    suggestedLocale,
    suggestedLocaleSource,
  ]);

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }
    const createdLinks = [];
    const origin = window.location.origin;
    const canonicalHref = `${origin}${locale === DEFAULT_LANGUAGE_CODE ? "/" : `/l/${encodeURIComponent(locale)}`}`;
    const appendHeadLink = ({ rel, href, hreflang = "" }) => {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      if (hreflang) {
        link.hreflang = hreflang;
      }
      link.dataset.mincoolLocaleHead = "true";
      document.head.appendChild(link);
      createdLinks.push(link);
    };

    appendHeadLink({ rel: "canonical", href: canonicalHref });
    appendHeadLink({ rel: "alternate", href: `${origin}/`, hreflang: "x-default" });
    appendHeadLink({ rel: "alternate", href: `${origin}/`, hreflang: DEFAULT_LANGUAGE_CODE });
    for (const language of getUiLanguageCatalog()) {
      if (language.code === DEFAULT_LANGUAGE_CODE) {
        continue;
      }
      appendHeadLink({
        rel: "alternate",
        href: `${origin}/l/${encodeURIComponent(language.code)}`,
        hreflang: language.code,
      });
    }

    return () => {
      createdLinks.forEach((link) => link.remove());
    };
  }, [locale]);

  function handleLocaleChange(nextLocale, source = "switcher") {
    const normalizedLocale = normalizeUiLanguageCode(nextLocale);
    if (source === "prompt" && !prototypeEnabled) {
      trackEvent("locale_prompt_selected", {
        language_code: normalizedLocale,
        source: suggestedLocaleSource,
        country_code: runtimeConfig?.countryCode || "",
        entry_surface: shareLinkLanguageCode ? "share_link" : "direct",
      });
    }
    setLocale(normalizedLocale);
    setDismissedSuggestion(true);
    navigate({
      pathname: normalizedLocale === DEFAULT_LANGUAGE_CODE
        ? "/"
        : `/l/${encodeURIComponent(normalizedLocale)}`,
      search: prototypeEnabled ? location.search : "",
    }, { replace: true });
  }

  return (
    <div id="top" className={`min-landing min-h-screen${introPending ? " is-intro-pending" : ""}`}>
      {showIntro ? <LandingLoadScreen onExitStart={handleIntroExit} /> : null}
      {showLocalePrompt ? (
        <div className="border-b border-border/70 bg-muted/40 px-4 py-3 text-sm">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-muted-foreground">
              {t("landing.locale_prompt", { language: getLanguageLabel(suggestedLocale) })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                className="h-9 rounded-full"
                onClick={() => handleLocaleChange(suggestedLocale, "prompt")}
              >
                {t("landing.use_language", { language: getLanguageLabel(suggestedLocale) })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 rounded-full text-muted-foreground"
                onClick={() => setDismissedSuggestion(true)}
              >
                {t("landing.keep_english")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <Hero
        onStart={handleStart}
        onSignIn={handleSignIn}
        onLocaleChange={handleLocaleChange}
        signedInNavItems={signedInNavItems}
        countryCode={runtimeConfig?.countryCode || ""}
        browserLanguages={browserLanguages}
        shareLinkLanguageCode={shareLinkLanguageCode}
        prototypeEnabled={instantLink.armed || instantLink.enabled}
        onPrototypeAwaken={handleInstantLinkAwaken}
      />
      {referralCode && shareLinkEntry ? (
        <p className="landing-shell py-4 text-center text-sm font-semibold" data-testid="referral-offer">
          {tx("One free month, plus a referral week")}
        </p>
      ) : null}
      {instantLink.enabled ? (
        <InstantQrPrototypePanel
          designModeEnabled={prototypeEnabled}
          awakeSignal={instantLink.awakeSignal}
          polling={instantLink.polling}
          qrImageSrc={instantLink.qrImageSrc}
          qrReady={instantLink.qrReady}
          recovering={instantLink.recovering}
          state={instantLink.state}
          onActivate={instantLink.onActivate}
          onConfigure={() => navigate("/auth?context=linked")}
          onOpenMin={() => navigate("/p")}
          onUsePhone={(options) => instantLink.onStateChange?.("phone", options)}
        />
      ) : null}
      <InboundMagic
        prototypeAwakeSignal={instantLink.awakeSignal}
        prototypeEnabled={instantLink.enabled}
        prototypePairingCode={instantLink.pairingCode}
        prototypePolling={instantLink.polling}
        prototypeQrImageSrc={instantLink.qrImageSrc}
        prototypeQrReady={instantLink.qrReady}
        prototypeRecovering={instantLink.recovering}
        prototypeState={instantLink.state}
        onCanonicalLink={() => handleStart("fun")}
        onPrototypeActivate={instantLink.onActivate}
        onPrototypeOpenWhatsApp={instantLink.onOpenWhatsApp}
        onPrototypeRequestCode={instantLink.onRequestCode}
        onPrototypeStateChange={instantLink.onStateChange}
        onPrototypeConfigure={() => navigate("/auth?context=linked")}
        onPrototypeOpenMin={() => navigate("/p")}
      />
      <UseCases />
      <Pricing onStart={handleStart} />
      <section className="landing-shell py-8 text-center">
        <h2 className="font-display text-2xl font-bold">{tx("Your AI agent, connected to WhatsApp")}</h2>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">{tx("Use your own AI agent for WhatsApp summaries, messages and daily digests.")}</p>
        <Button className="mt-4 rounded-full" onClick={() => navigate("/agent")}>{tx("Connect my agent")}</Button>
      </section>
      <Footer />
      <SignedInCtaDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setPendingIntent(null);
            setAccountDestination("link");
            setDialogLoading(false);
          }
        }}
        title={dialogState.title}
        description={dialogState.description}
        primaryAction={dialogState.primaryAction}
        accountAction={dialogState.accountAction}
        onPrimaryAction={() => {
          setDialogOpen(false);
          navigate(dialogState.primaryAction.href);
          setPendingIntent(null);
        }}
        onAccountAction={() => {
          if (!dialogState.accountAction) {
            return;
          }
          setDialogOpen(false);
          navigate(dialogState.accountAction.href);
          setPendingIntent(null);
        }}
        onLogoutAndContinue={() => void handleLogoutAndContinue()}
        onCancel={() => {
          setDialogOpen(false);
          setPendingIntent(null);
        }}
        loading={dialogLoading}
        logoutLoading={dialogLogoutLoading}
      />
    </div>
  );
}
